import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

const DOME_R = 310;
const DOME_CENTER_Y = -10;               // center of the dome (where rotation pivot is)
const TARGET_SKYLINE_WIDTH = DOME_R * 1.8;

// ===== SKYLINE VISUAL CONTROLS =====
const SKYLINE_COLOR = 0xedcfb0;
const SKYLINE_EMISSIVE = 0xedcfb0;
const SKYLINE_OPACITY = 1;

// Texture controls
const USE_TEXTURE = true;
const TEXTURE_PATH = '/sand.jpg';
const TEXTURE_SCALE_U = 10;
const TEXTURE_SCALE_V = 10;
const TINT_TEXTURE_WITH_COLOR = true;

// ===== POSITION CONTROLS =====
// Offsets relative to the auto‑placed position (in world space)
const MODEL_OFFSET_X = 0;
const MODEL_OFFSET_Y = 0;
const MODEL_OFFSET_Z = 0;

// ===== ROTATION CONTROLS =====
// Rotation around the dome center (Y axis) in degrees – this makes the skyline orbit the shield
const ORBIT_Y = 0;   // 0° = default orientation, 90° = rotated 90° around center

// Local rotation of the model itself (in degrees, applied after orbiting)
const MODEL_ROTATION_X = 0;
const MODEL_ROTATION_Y = 0;
const MODEL_ROTATION_Z = 0;

// ===== MATERIAL PROPERTIES =====
const MODEL_METALNESS = 1;
const MODEL_ROUGHNESS = 0.8;
// ====================================

// Legacy vertical offset – kept for compatibility, but you can now use MODEL_OFFSET_Y instead
const VERTICAL_OFFSET = 62;

export class ParticleSystem {
  constructor(scene) {
    this.scene = scene;
    this.model = null;
    this._isLoading = true;
    this.time = 0;
  }

  async init() {
    try {
      const loader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      loader.setDRACOLoader(dracoLoader);

      const gltf = await new Promise((resolve, reject) => {
        loader.load('/SKYLINE.glb', resolve, undefined, reject);
      });

      const model = gltf.scene;

      // Compute bounding box and auto‑scale/center
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const modelWidth = Math.max(size.x, size.z);
      const autoScale = TARGET_SKYLINE_WIDTH / modelWidth;

      model.scale.set(autoScale, autoScale, autoScale);

      // Compute desired world position (auto‑centered + offsets)
      const worldPos = new THREE.Vector3(
        -center.x * autoScale + MODEL_OFFSET_X,
        -center.y * autoScale + VERTICAL_OFFSET + MODEL_OFFSET_Y,
        -center.z * autoScale + MODEL_OFFSET_Z
      );

      // Create a pivot group at the dome center
      const pivot = new THREE.Group();
      pivot.position.set(0, DOME_CENTER_Y, 0);

      // Convert world position to position relative to pivot
      const relativePos = worldPos.clone().sub(pivot.position);
      model.position.copy(relativePos);

      // Apply local rotation to the model (if any)
      model.rotation.x = THREE.MathUtils.degToRad(MODEL_ROTATION_X);
      model.rotation.y = THREE.MathUtils.degToRad(MODEL_ROTATION_Y);
      model.rotation.z = THREE.MathUtils.degToRad(MODEL_ROTATION_Z);

      // Apply orbit rotation to the pivot (around dome center)
      pivot.rotation.y = THREE.MathUtils.degToRad(ORBIT_Y);

      // Load and configure texture if enabled
      let customTexture = null;
      if (USE_TEXTURE) {
        const textureLoader = new THREE.TextureLoader();
        customTexture = textureLoader.load(TEXTURE_PATH);
        customTexture.wrapS = THREE.RepeatWrapping;
        customTexture.wrapT = THREE.RepeatWrapping;
        customTexture.repeat.set(TEXTURE_SCALE_U, TEXTURE_SCALE_V);
      }

      // Apply material customizations to the model
      model.traverse(child => {
        if (child.isMesh) {
          if (customTexture) {
            child.material.map = customTexture;
            child.material.color.setHex(TINT_TEXTURE_WITH_COLOR ? SKYLINE_COLOR : 0xffffff);
          } else {
            child.material.color.setHex(SKYLINE_COLOR);
          }

          child.material.emissive.setHex(SKYLINE_EMISSIVE);
          child.material.transparent = true;
          child.material.opacity = SKYLINE_OPACITY;

          if (child.material.metalness !== undefined) {
            child.material.metalness = MODEL_METALNESS;
          }
          if (child.material.roughness !== undefined) {
            child.material.roughness = MODEL_ROUGHNESS;
          }
          if (child.material.emissiveIntensity !== undefined) {
            child.material.emissiveIntensity = 0.1;
          }
        }
      });

      // Add model to pivot, then pivot to scene
      pivot.add(model);
      this.scene.add(pivot);

      this.model = model;
      this.pivot = pivot; // store if needed
      this._isLoading = false;
      dracoLoader.dispose();

      console.log('✅ Skyline model orbiting around dome center. ORBIT_Y:', ORBIT_Y);
    } catch (err) {
      console.error('❌ Skyline load failed:', err);
      this._isLoading = false;
    }
  }

  update(time, smoothStage, curlSpeed = 1.0) {
    this.time = time; // store for shader use if needed
    // If you want to animate the orbit, you could update pivot.rotation.y here
    // e.g., this.pivot.rotation.y += 0.001;
  }
}